import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./Chat.css";
import axios from "axios";
import API_BASE_URL from "../../config";
import { notify } from "../../components/Notify";

export default function Chat() {
  const [devices, setDevices] = useState([]);
  const [waNumbers, setWaNumbers] = useState([]);
  const [imHandles, setImHandles] = useState([]);
  const [channel, setChannel] = useState("sms"); // 'sms' | 'whatsapp' | 'imessage'
  const [selectedDevice, setSelectedDevice] = useState("");
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [selectedSim, setSelectedSim] = useState("SIM1");
  const [sending, setSending] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [mobileView, setMobileView] = useState("list"); // "list" or "chat"
  // Delete state
  const [deleteConvoPhone, setDeleteConvoPhone] = useState(null); // confirm delete conversation
  const [deletingMsg, setDeletingMsg] = useState(null); // { id, direction }
  const [hoveredMsgId, setHoveredMsgId] = useState(null);

  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const channelInitRef = useRef(false);

  const getUserToken = () => localStorage.getItem("userToken");

  const getHeaders = useCallback(() => {
    const token = getUserToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // A selected source is either an SMS device (fingerprint) or a WhatsApp number ("wa:<id>").
  const isWA = (sel) => typeof sel === "string" && sel.startsWith("wa:");
  const waId = (sel) => sel.replace(/^wa:/, "");
  const isIM = (sel) => typeof sel === "string" && sel.startsWith("im:");
  const imId = (sel) => sel.replace(/^im:/, "");

  // Redirect if no token
  useEffect(() => {
    if (!getUserToken()) {
      window.location.href = "/login";
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Get allowed SIMs for the currently selected device
  const getAllowedSims = useCallback(() => {
    const device = devices.find((d) => d.device_fingerprint === selectedDevice);
    const sel = (device?.selected_sim || "SIM1").toUpperCase();
    if (sel === "BOTH") return ["SIM1", "SIM2"];
    return [sel];
  }, [devices, selectedDevice]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    setLoadingDevices(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/devices`, {
        headers: getHeaders(),
        timeout: 15000,
      });
      const deviceList = response.data.devices || response.data || [];
      setDevices(deviceList);
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setLoadingDevices(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Fetch connected WhatsApp numbers as selectable chat sources (ignore if not subscribed).
  useEffect(() => {
    if (!getUserToken()) return;
    axios
      .get(`${API_BASE_URL}/whatsapp/numbers`, { headers: getHeaders(), timeout: 15000 })
      .then((res) => setWaNumbers((res.data.numbers || []).filter((n) => n.status === "connected")))
      .catch(() => setWaNumbers([]));
  }, [getHeaders]);

  // Fetch connected iMessage handles (Macs) as selectable chat sources.
  useEffect(() => {
    if (!getUserToken()) return;
    axios
      .get(`${API_BASE_URL}/imessage/handles`, { headers: getHeaders(), timeout: 15000 })
      .then((res) => setImHandles(res.data.handles || []))
      .catch(() => setImHandles([]));
  }, [getHeaders]);

  // Pick a sensible initial channel once sources load (SMS if any device, else WhatsApp).
  useEffect(() => {
    if (channelInitRef.current) return;
    if (devices.length > 0) {
      channelInitRef.current = true;
      setChannel("sms");
    } else if (waNumbers.length > 0) {
      channelInitRef.current = true;
      setChannel("whatsapp");
    }
  }, [devices, waNumbers]);

  // When the channel changes, select that channel's first source and reset the view.
  useEffect(() => {
    if (channel === "sms") {
      setSelectedDevice(devices[0]?.device_fingerprint || "");
    } else if (channel === "whatsapp") {
      setSelectedDevice(waNumbers[0] ? "wa:" + waNumbers[0].id : "");
    } else if (channel === "imessage") {
      setSelectedDevice(imHandles[0] ? "im:" + imHandles[0].id : "");
    } else {
      setSelectedDevice("");
    }
    setSelectedPhone(null);
    setMessages([]);
    setConversations([]);
    setFilteredConversations([]);
    setIsNewMessage(false);
  }, [channel, devices, waNumbers, imHandles]);

  // Fetch conversations when device changes
  const fetchConversations = useCallback(async () => {
    if (!selectedDevice) return;
    setLoadingConversations(true);
    try {
      if (isWA(selectedDevice)) {
        const res = await axios.get(
          `${API_BASE_URL}/whatsapp/conversations?number_id=${waId(selectedDevice)}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        setConversations(
          (res.data.conversations || []).map((c) => ({
            phone: c.contact,
            lastMessage: c.last_message,
            last_message_at: c.last_message_at,
          }))
        );
      } else if (isIM(selectedDevice)) {
        const res = await axios.get(
          `${API_BASE_URL}/imessage/conversations?handle_id=${imId(selectedDevice)}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        setConversations(
          (res.data.conversations || []).map((c) => ({
            phone: c.contact,
            lastMessage: c.last_message,
            last_message_at: c.last_message_at,
          }))
        );
      } else {
        const response = await axios.get(
          `${API_BASE_URL}/chat/conversations?device_fingerprint=${selectedDevice}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        const convos = response.data.conversations || response.data || [];
        setConversations(convos);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  }, [selectedDevice, getHeaders]);

  useEffect(() => {
    if (selectedDevice) {
      fetchConversations();
      setSelectedPhone(null);
      setMessages([]);
      setIsNewMessage(false);
      setMobileView("list");
      // Auto-set SIM to the first allowed SIM for this device
      const device = devices.find((d) => d.device_fingerprint === selectedDevice);
      const sel = (device?.selected_sim || "SIM1").toUpperCase();
      if (sel === "BOTH") {
        setSelectedSim("SIM1");
      } else {
        setSelectedSim(sel);
      }
    }
  }, [selectedDevice, fetchConversations, devices]);

  // Filter conversations by search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredConversations(
        conversations.filter((c) =>
          (c.phone || c.phone_number || "").toLowerCase().includes(q)
        )
      );
    }
  }, [searchQuery, conversations]);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async () => {
    if (!selectedDevice || !selectedPhone) return;
    try {
      if (isWA(selectedDevice)) {
        const res = await axios.get(
          `${API_BASE_URL}/whatsapp/thread?number_id=${waId(selectedDevice)}&contact=${encodeURIComponent(selectedPhone)}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        setMessages(
          (res.data.messages || []).map((m) => ({
            id: m.id,
            body: m.content,
            type: m.type,
            media_url: m.media_url,
            direction: m.direction,
            status: m.status,
            sim_slot: null,
            created_at: m.created_at,
          }))
        );
      } else if (isIM(selectedDevice)) {
        const res = await axios.get(
          `${API_BASE_URL}/imessage/thread?handle_id=${imId(selectedDevice)}&contact=${encodeURIComponent(selectedPhone)}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        setMessages(
          (res.data.messages || []).map((m) => ({
            id: m.id,
            body: m.content,
            type: m.type,
            media_url: m.media_url,
            direction: m.direction,
            status: m.status,
            sim_slot: null,
            created_at: m.created_at,
          }))
        );
      } else {
        const response = await axios.get(
          `${API_BASE_URL}/chat/messages?device_fingerprint=${selectedDevice}&phone=${encodeURIComponent(selectedPhone)}`,
          { headers: getHeaders(), timeout: 15000 }
        );
        setMessages(response.data.messages || response.data || []);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, [selectedDevice, selectedPhone, getHeaders]);

  useEffect(() => {
    if (selectedPhone) {
      setLoadingMessages(true);
      fetchMessages().finally(() => setLoadingMessages(false));
    }
  }, [selectedPhone, fetchMessages]);

  // Poll for new messages every 10 seconds — cleanup on unmount or dependency change
  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (selectedPhone && selectedDevice) {
      pollIntervalRef.current = setInterval(() => {
        fetchMessages();
        fetchConversations();
      }, 10000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [selectedPhone, selectedDevice, fetchMessages, fetchConversations]);

  // Handle selecting a conversation
  const handleSelectConversation = (phone) => {
    setSelectedPhone(phone);
    setIsNewMessage(false);
    setNewPhoneNumber("");
    setMobileView("chat");
  };

  // Handle new message button
  const handleNewMessage = () => {
    setIsNewMessage(true);
    setSelectedPhone(null);
    setMessages([]);
    setNewPhoneNumber("");
    setMobileView("chat");
  };

  // Handle back button on mobile
  const handleBackToList = () => {
    setMobileView("list");
  };

  // Delete a single message
  const handleDeleteMessage = async (msg) => {
    try {
      if (isWA(selectedDevice)) {
        await axios.delete(`${API_BASE_URL}/whatsapp/messages/${msg.id}`, {
          headers: getHeaders(),
          timeout: 15000,
        });
      } else if (isIM(selectedDevice)) {
        await axios.delete(`${API_BASE_URL}/imessage/messages/${msg.id}`, {
          headers: getHeaders(),
          timeout: 15000,
        });
      } else {
        const direction = (msg.direction === "outbound" || msg.direction === "sent" || msg.type === "outbound")
          ? "outbound" : "inbound";
        await axios.delete(`${API_BASE_URL}/chat/message`, {
          headers: getHeaders(),
          data: { id: msg.id, direction },
          timeout: 15000,
        });
      }
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      fetchConversations();
    } catch (error) {
      console.error("Error deleting message:", error);
      notify("Failed to delete message.", "error");
    } finally {
      setDeletingMsg(null);
    }
  };

  // Delete full conversation
  const handleDeleteConversation = async () => {
    if (!deleteConvoPhone || !selectedDevice) return;
    try {
      if (isWA(selectedDevice)) {
        await axios.delete(`${API_BASE_URL}/whatsapp/conversation`, {
          headers: getHeaders(),
          data: { number_id: waId(selectedDevice), contact: deleteConvoPhone },
          timeout: 15000,
        });
      } else if (isIM(selectedDevice)) {
        await axios.delete(`${API_BASE_URL}/imessage/conversation`, {
          headers: getHeaders(),
          data: { handle_id: imId(selectedDevice), contact: deleteConvoPhone },
          timeout: 15000,
        });
      } else {
        await axios.delete(`${API_BASE_URL}/chat/conversation`, {
          headers: getHeaders(),
          data: { device_fingerprint: selectedDevice, phone: deleteConvoPhone },
          timeout: 15000,
        });
      }
      setDeleteConvoPhone(null);
      setSelectedPhone(null);
      setMessages([]);
      fetchConversations();
    } catch (error) {
      console.error("Error deleting conversation:", error);
      notify("Failed to delete conversation.", "error");
    }
  };

  // Send message
  const handleSend = async () => {
    const phone = isNewMessage ? newPhoneNumber.trim() : selectedPhone;
    if (!phone || !messageText.trim() || !selectedDevice) return;

    setSending(true);
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      body: messageText,
      direction: "outbound",
      sim_slot: (isWA(selectedDevice) || isIM(selectedDevice)) ? null : selectedSim,
      status: "pending",
      timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    const sentText = messageText;
    setMessageText("");

    try {
      if (isWA(selectedDevice)) {
        await axios.post(
          `${API_BASE_URL}/whatsapp/send`,
          { number_id: waId(selectedDevice), to: phone, type: "text", content: sentText },
          { headers: getHeaders(), timeout: 20000 }
        );
      } else if (isIM(selectedDevice)) {
        await axios.post(
          `${API_BASE_URL}/imessage/send`,
          { handle_id: imId(selectedDevice), to: phone, content: sentText },
          { headers: getHeaders(), timeout: 20000 }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/chat/send`,
          {
            device_fingerprint: selectedDevice,
            phone: phone,
            message: sentText,
            sim_slot: selectedSim,
          },
          { headers: getHeaders(), timeout: 15000 }
        );
      }

      // If this was a new message, transition to a conversation
      if (isNewMessage) {
        setSelectedPhone(phone);
        setIsNewMessage(false);
        setNewPhoneNumber("");
        fetchConversations();
      }

      // Refresh messages to get server-confirmed status
      setTimeout(() => fetchMessages(), 1000);
    } catch (error) {
      console.error("Error sending message:", error);
      // Mark the optimistic message as failed
      setMessages((prev) =>
        prev.map((m) =>
          m.id === optimisticMsg.id ? { ...m, status: "failed" } : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  // ── WhatsApp media / voice (only used when a WhatsApp source is selected) ──
  const sendMediaMessage = async ({ url, type, caption, fileName }) => {
    const phone = isNewMessage ? newPhoneNumber.trim() : selectedPhone;
    if (!phone) return;
    const optimistic = {
      id: `temp-${Date.now()}`,
      body: caption || "",
      type,
      media_url: url,
      file_name: fileName,
      direction: "outbound",
      status: "pending",
      sim_slot: null,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      await axios.post(
        `${API_BASE_URL}/whatsapp/send`,
        { number_id: waId(selectedDevice), to: phone, type, content: caption || "", media_url: url },
        { headers: getHeaders(), timeout: 30000 }
      );
      if (isNewMessage) {
        setSelectedPhone(phone);
        setIsNewMessage(false);
        setNewPhoneNumber("");
        fetchConversations();
      }
      setTimeout(() => fetchMessages(), 1200);
    } catch (error) {
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? { ...m, status: "failed" } : m)));
      notify(error.response?.data?.error || "Failed to send media.", "error");
    }
  };

  const uploadFile = async (file, filename) => {
    const fd = new FormData();
    fd.append("file", file, filename || file.name);
    const res = await axios.post(`${API_BASE_URL}/whatsapp/media`, fd, {
      headers: getHeaders(), // axios sets multipart boundary automatically
      timeout: 60000,
    });
    return res.data; // { url, type, fileName }
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadFile(file);
      await sendMediaMessage({ url: up.url, type: up.type, caption: messageText, fileName: up.fileName });
      setMessageText("");
    } catch (error) {
      notify("Upload failed.", "error");
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        setUploading(true);
        try {
          const up = await uploadFile(blob, "voice-note.webm");
          await sendMediaMessage({ url: up.url, type: "voice", caption: "", fileName: "Voice note" });
        } catch (error) {
          notify("Voice upload failed.", "error");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (error) {
      notify("Microphone access denied.", "error");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  // Only allow safe URL schemes for remote-controlled media (inbound messages
  // can carry attacker-supplied media_url — block javascript:/data: etc.).
  const safeMediaUrl = (u) => {
    if (typeof u !== "string" || !u) return "";
    try {
      const parsed = new URL(u, window.location.origin);
      return ["http:", "https:", "blob:"].includes(parsed.protocol) ? u : "";
    } catch {
      return "";
    }
  };

  // Render a message body: media (image/video/audio/voice/file) or plain text.
  const renderMessageBody = (msg) => {
    const url = safeMediaUrl(msg.media_url);
    if (url) {
      const t = msg.type;
      if (t === "image") return (<><img src={url} alt="" className="chat-media-img" />{msg.body ? <div className="chat-media-caption">{msg.body}</div> : null}</>);
      if (t === "video") return (<><video src={url} controls className="chat-media-img" />{msg.body ? <div className="chat-media-caption">{msg.body}</div> : null}</>);
      if (t === "audio" || t === "voice" || t === "ptt") return (<audio src={url} controls className="chat-media-audio" />);
      return (<a href={url} target="_blank" rel="noreferrer" className="chat-media-file"><i className="bi bi-file-earmark-arrow-down" /> {msg.file_name || "Download file"}</a>);
    }
    return msg.body || msg.message || msg.text;
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format timestamp
  const formatTime = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Truncate text
  const truncate = (text, maxLen = 40) => {
    if (!text) return "";
    return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
  };

  // Get status icon (WhatsApp-style ticks; reused for all channels)
  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <i className="bi bi-check2 chat-status-icon sent" title="Sent" />;
      case "delivered":
        return <i className="bi bi-check2-all chat-status-icon sent" title="Delivered" />;
      case "read":
        return <i className="bi bi-check2-all chat-status-icon read" title="Read" />;
      case "failed":
        return <i className="bi bi-exclamation-circle chat-status-icon failed" title="Failed" />;
      case "queued":
      case "sending":
      case "pending":
      default:
        return <i className="bi bi-clock chat-status-icon pending" title="Pending" />;
    }
  };

  // Get device status indicator
  const getDeviceStatus = (device) => {
    const isOnline = device.status === "active" || device.is_online;
    return (
      <span
        className={`chat-device-status ${isOnline ? "online" : "offline"}`}
      />
    );
  };

  return (
    <div className="chat-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="chat-page">
          {/* Channel Tabs */}
          <div className="chat-channel-tabs">
            <button
              className={`chat-channel-tab ${channel === "sms" ? "active sms" : ""}`}
              onClick={() => setChannel("sms")}
            >
              <i className="bi bi-phone-fill" /> SMS
            </button>
            <button
              className={`chat-channel-tab ${channel === "whatsapp" ? "active whatsapp" : ""}`}
              onClick={() => setChannel("whatsapp")}
            >
              <i className="bi bi-whatsapp" /> WhatsApp
            </button>
            <button
              className={`chat-channel-tab ${channel === "imessage" ? "active imessage" : ""}`}
              onClick={() => setChannel("imessage")}
            >
              <i className="bi bi-chat-fill" /> iMessage
            </button>
          </div>

          {/* Source selector for the active channel */}
          <div className="chat-device-selector">
            {channel === "imessage" ? (
              imHandles.length === 0 ? (
                <span className="text-muted">No connected iMessage handles — add a Mac in Ops.</span>
              ) : (
                <select
                  className="form-select"
                  value={isIM(selectedDevice) ? selectedDevice : ""}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                >
                  {imHandles.map((h) => (
                    <option key={"im:" + h.id} value={"im:" + h.id}>
                      {(h.label || "iMessage")}{h.handle ? ` (${h.handle})` : ""}
                    </option>
                  ))}
                </select>
              )
            ) : channel === "sms" ? (
              loadingDevices ? (
                <span className="text-muted">Loading devices...</span>
              ) : devices.length === 0 ? (
                <span className="text-muted">No SMS devices found.</span>
              ) : (
                <select
                  className="form-select"
                  value={isWA(selectedDevice) ? "" : selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                >
                  {devices.map((d) => {
                    const sim = (d.selected_sim || "SIM1").toUpperCase();
                    const simLabel = sim === "BOTH" ? "SIM1+SIM2" : sim;
                    return (
                      <option key={d.device_fingerprint} value={d.device_fingerprint}>
                        {d.device_name || d.device_fingerprint}
                        {d.status === "active" ? " (Active)" : " (Inactive)"}
                        {" — " + simLabel}
                      </option>
                    );
                  })}
                </select>
              )
            ) : waNumbers.length === 0 ? (
              <span className="text-muted">
                No connected WhatsApp number — link one in the WhatsApp section.
              </span>
            ) : (
              <select
                className="form-select"
                value={isWA(selectedDevice) ? selectedDevice : ""}
                onChange={(e) => setSelectedDevice(e.target.value)}
              >
                {waNumbers.map((n) => (
                  <option key={"wa:" + n.id} value={"wa:" + n.id}>
                    {(n.label || "WhatsApp")}{n.phone ? " (" + n.phone + ")" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className={`chat-layout chat-ch-${channel}`}>
            {/* Left Panel: Conversation List */}
            <div className={`chat-conversations-panel ${mobileView === "chat" ? "mobile-hidden" : ""}`}>
              <div className="chat-conversations-header">
                <button
                  className="btn btn-primary btn-sm chat-new-msg-btn"
                  onClick={handleNewMessage}
                >
                  <i className="bi bi-plus-lg" /> New Message
                </button>
                <div className="chat-search-box">
                  <i className="bi bi-search chat-search-icon" />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="chat-conversations-list">
                {loadingConversations ? (
                  <div className="chat-empty-state">Loading conversations...</div>
                ) : filteredConversations.length === 0 ? (
                  <div className="chat-empty-state">
                    {searchQuery ? "No matching conversations" : "No conversations yet"}
                  </div>
                ) : (
                  filteredConversations.map((convo) => {
                    const phone = convo.phone || convo.phone_number;
                    const isActive = selectedPhone === phone && !isNewMessage;
                    return (
                      <div
                        key={phone}
                        className={`chat-conversation-item ${isActive ? "active" : ""}`}
                        onClick={() => handleSelectConversation(phone)}
                      >
                        <div className="chat-conversation-avatar">
                          <i className="bi bi-person-circle" />
                        </div>
                        <div className="chat-conversation-info">
                          <div className="chat-conversation-top">
                            <span className="chat-conversation-phone">{phone}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span className="chat-conversation-time">
                                {formatTime(convo.lastMessageAt || convo.last_message_at || convo.timestamp)}
                              </span>
                              <button
                                className="chat-delete-convo-btn"
                                title="Delete conversation"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConvoPhone(phone);
                                }}
                              >
                                <i className="bi bi-trash3" />
                              </button>
                            </div>
                          </div>
                          <div className="chat-conversation-preview">
                            {truncate(convo.lastMessage || convo.last_message || "")}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Panel: Messages */}
            <div className={`chat-messages-panel ${mobileView === "list" ? "mobile-hidden" : ""}`}>
              {!selectedPhone && !isNewMessage ? (
                <div className="chat-empty-state chat-empty-center">
                  <i className="bi bi-chat-dots chat-empty-icon" />
                  <p>Select a conversation or start a new one</p>
                </div>
              ) : (
                <>
                  {/* Chat header */}
                  <div className="chat-messages-header">
                    <button
                      className="chat-back-btn"
                      onClick={handleBackToList}
                    >
                      <i className="bi bi-arrow-left" />
                    </button>
                    {isNewMessage ? (
                      <div className="chat-new-phone-input">
                        <i className="bi bi-telephone" />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter phone number..."
                          value={newPhoneNumber}
                          onChange={(e) => setNewPhoneNumber(e.target.value)}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div className="chat-messages-header-info">
                        <div className="chat-header-avatar">
                          <i className="bi bi-person-fill" />
                        </div>
                        <span className="chat-messages-header-phone">{selectedPhone}</span>
                      </div>
                    )}
                    {selectedPhone && (
                      <button
                        className="chat-delete-header-btn"
                        title="Delete this conversation"
                        onClick={() => setDeleteConvoPhone(selectedPhone)}
                      >
                        <i className="bi bi-trash3" />
                      </button>
                    )}
                  </div>

                  {/* Messages area */}
                  <div className="chat-messages-area">
                    {loadingMessages ? (
                      <div className="chat-empty-state">Loading messages...</div>
                    ) : messages.length === 0 ? (
                      <div className="chat-empty-state">
                        {isNewMessage
                          ? "Type a message to start the conversation"
                          : "No messages in this conversation"}
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isOutbound =
                          msg.direction === "outbound" ||
                          msg.direction === "sent" ||
                          msg.type === "outbound";
                        const isTemp = String(msg.id).startsWith("temp-");
                        return (
                          <div
                            key={msg.id || idx}
                            className={`chat-bubble-row ${isOutbound ? "outbound" : "inbound"}`}
                            onMouseEnter={() => setHoveredMsgId(msg.id)}
                            onMouseLeave={() => setHoveredMsgId(null)}
                          >
                            <div className={`chat-bubble ${isOutbound ? "outbound" : "inbound"}`}>
                              <div className="chat-bubble-body">{renderMessageBody(msg)}</div>
                              <div className="chat-bubble-meta">
                                {msg.sim_slot && (
                                  <span className={`chat-sim-badge ${(msg.sim_slot || "SIM1").toLowerCase()}`}>
                                    {msg.sim_slot}
                                  </span>
                                )}
                                <span className="chat-bubble-time">
                                  {formatTime(msg.timestamp || msg.created_at)}
                                </span>
                                {isOutbound && getStatusIcon(msg.status)}
                                {!isTemp && hoveredMsgId === msg.id && (
                                  <button
                                    className="chat-msg-delete-btn"
                                    title="Delete message"
                                    onClick={() => setDeletingMsg(msg)}
                                  >
                                    <i className="bi bi-x-circle" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  <div className="chat-composer">
                    {!isWA(selectedDevice) && !isIM(selectedDevice) && (
                    <div className="chat-sim-selector">
                      {getAllowedSims().map((sim) => (
                        <label key={sim} className={`chat-sim-option ${selectedSim === sim ? "active" : ""}`}>
                          <input
                            type="radio"
                            name="sim"
                            value={sim}
                            checked={selectedSim === sim}
                            onChange={() => setSelectedSim(sim)}
                          />
                          {sim === "SIM1" ? "SIM 1" : "SIM 2"}
                        </label>
                      ))}
                      {getAllowedSims().length === 1 && (
                        <span className="chat-sim-note text-muted">
                          Only {getAllowedSims()[0]} is enabled on device
                        </span>
                      )}
                    </div>
                    )}
                    <div className="chat-input-row">
                      {isWA(selectedDevice) && (
                        <>
                          <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFilePick}
                          />
                          <button
                            className="btn chat-attach-btn"
                            title="Attach file / media"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading || sending}
                          >
                            <i className="bi bi-paperclip" />
                          </button>
                          <button
                            className={`btn chat-mic-btn ${recording ? "recording" : ""}`}
                            title={recording ? "Stop & send" : "Record voice note"}
                            onClick={recording ? stopRecording : startRecording}
                            disabled={uploading}
                          >
                            <i className={`bi ${recording ? "bi-stop-circle-fill" : "bi-mic-fill"}`} />
                          </button>
                        </>
                      )}
                      <input
                        type="text"
                        className="form-control"
                        placeholder={uploading ? "Uploading…" : recording ? "Recording… tap stop to send" : "Type a message..."}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={sending || uploading || recording}
                      />
                      <button
                        className="btn btn-primary chat-send-btn"
                        onClick={handleSend}
                        disabled={sending || !messageText.trim()}
                      >
                        {sending ? (
                          <i className="bi bi-hourglass-split" />
                        ) : (
                          <i className="bi bi-send-fill" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Message Confirmation */}
      {deletingMsg && (
        <div className="modal-overlay" onClick={() => setDeletingMsg(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger">
              <i className="bi bi-trash3-fill" />
            </div>
            <h5>Delete Message?</h5>
            <p>This will permanently remove this message. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setDeletingMsg(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={() => handleDeleteMessage(deletingMsg)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Confirmation */}
      {deleteConvoPhone && (
        <div className="modal-overlay" onClick={() => setDeleteConvoPhone(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger">
              <i className="bi bi-trash3-fill" />
            </div>
            <h5>Delete Conversation?</h5>
            <p>All messages with <strong>{deleteConvoPhone}</strong> will be permanently deleted. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setDeleteConvoPhone(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={handleDeleteConversation}>Delete All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
